// src/oura/components/aeo/HistoryList.tsx
//
// AC: "History list per domain (newest first) with re-run." (#3065)
// Backed by GET /api/v1/assessments?domain=&limit= — newest first per the ticket.

import { useEffect, useState } from "react";
import { HistoryIcon, RefreshCwIcon, Loader2Icon, ChevronRightIcon } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../primitives/Card";
import { Button } from "../../primitives/Button";
import { Chip } from "../../primitives/Chip";
import { EmptyState } from "../../primitives/EmptyState";
import { listAssessments, type AeoAssessment, type AeoAssessmentStatus } from "@service/aeo";

const STATUS_CHIP: Record<AeoAssessmentStatus, string> = {
    queued: "border-blue-200 bg-blue-50 text-blue-700",
    running: "border-blue-200 bg-blue-50 text-blue-700",
    complete: "border-emerald-200 bg-emerald-50 text-emerald-700",
    partial: "border-amber-200 bg-amber-50 text-amber-700",
    failed: "border-rose-200 bg-rose-50 text-rose-700",
    // Neutral grey, not rose: a deliberately stopped run is a decision, not a
    // malfunction (ourteam-backend#3982).
    cancelled: "border-gray-200 bg-gray-50 text-gray-600",
};

const STATUS_LABEL: Record<AeoAssessmentStatus, string> = {
    queued: "Queued",
    running: "Running",
    complete: "Complete",
    partial: "Partial",
    failed: "Failed",
    cancelled: "Cancelled",
};

export function HistoryList({
    domain,
    activeId,
    onOpen,
    onRerun,
    refreshKey,
}: {
    domain: string;
    activeId?: string | null;
    onOpen: (assessmentId: string) => void;
    onRerun: (domain: string, businessName: string) => void;
    /** Bump to force a refetch (e.g. right after a new run starts). */
    refreshKey?: number;
}) {
    const [items, setItems] = useState<AeoAssessment[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!domain) {
            setItems([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        listAssessments(domain, 20)
            .then((list) => {
                if (!cancelled) setItems(list);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [domain, refreshKey]);

    return (
        <Card>
            <CardHeader
                title="History"
                description={domain ? `Past runs for ${domain}, newest first.` : "Runs for the current domain."}
                icon={HistoryIcon}
            />
            <CardBody className="pt-2">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={HistoryIcon}
                        title="No runs yet"
                        description="Once an assessment runs for this domain, it shows up here."
                    />
                ) : (
                    <div className="divide-y divide-gray-200">
                        {items.map((a) => (
                            <div
                                key={a.assessment_id}
                                className={`flex items-center justify-between gap-3 py-2.5 ${a.assessment_id === activeId ? "bg-blue-50/50" : ""}`}
                            >
                                <button
                                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                    onClick={() => onOpen(a.assessment_id)}
                                >
                                    <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300" />
                                    <div className="min-w-0">
                                        <div className="truncate text-sm text-gray-900">
                                            {new Date(a.created_at).toLocaleString()}
                                        </div>
                                        {a.results && (
                                            <div className="truncate text-xs text-gray-500">
                                                {a.results.maturity_label} · ${a.results.actual_cost_usd.toFixed(2)}
                                            </div>
                                        )}
                                    </div>
                                </button>
                                <Chip className={STATUS_CHIP[a.status]}>{STATUS_LABEL[a.status]}</Chip>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    // The list is already scoped to one domain, and the BE
                                    // omits `domain` from the assessment payload — so the
                                    // prop is the authoritative value here, not `a.domain`.
                                    onClick={() => onRerun(domain, a.business_name ?? "")}
                                >
                                    <RefreshCwIcon className="h-3.5 w-3.5" />
                                    Re-run
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
